import { DOCUMENT } from '@angular/common';
import { Injectable, inject, signal } from '@angular/core';

export interface UserInteractionSnapshot {
    type: string;
    target: string;
    timestamp: string;
    url: string;
    inputLength?: number;
}

@Injectable({ providedIn: 'root' })
export class UserInteractionService {
    private readonly document = inject(DOCUMENT);
    private readonly interactionSignal = signal<UserInteractionSnapshot | null>(null);

    readonly lastInteraction = this.interactionSignal.asReadonly();

    constructor() {
        this.bindListeners();
    }

    private bindListeners(): void {
        const handler = (event: Event) => this.recordInteraction(event);
        this.document.addEventListener('click', handler, { capture: true, passive: true });
        this.document.addEventListener('keydown', handler, { capture: true, passive: true });
        this.document.addEventListener('input', handler, { capture: true, passive: true });
        this.document.addEventListener('change', handler, { capture: true, passive: true });
    }

    private recordInteraction(event: Event): void {
        const target = event.target as HTMLElement | null;
        const snapshot: UserInteractionSnapshot = {
            type: event.type,
            target: this.describeTarget(target),
            timestamp: new Date().toISOString(),
            url: window.location.href
        };

        const inputLength = this.getInputLength(target);
        if (inputLength !== undefined) {
            snapshot.inputLength = inputLength;
        }

        this.interactionSignal.set(snapshot);
    }

    private describeTarget(target: HTMLElement | null): string {
        if (!target) {
            return 'unknown';
        }

        const tagName = target.tagName.toLowerCase();
        const idPart = target.id ? `#${target.id}` : '';
        const classPart = this.formatClassList(target.classList);
        const namePart = this.formatNameAttribute(target);
        const rolePart = this.formatRoleAttribute(target);
        const textPart = this.formatTargetText(target);

        return `${tagName}${idPart}${classPart}${namePart}${rolePart}${textPart}`.trim();
    }

    private formatClassList(classList: DOMTokenList): string {
        const classes = Array.from(classList).slice(0, 3);
        return classes.length ? `.${classes.join('.')}` : '';
    }

    private formatNameAttribute(target: HTMLElement): string {
        const name = (target as HTMLInputElement).name;
        return name ? `[name=${name}]` : '';
    }

    private formatRoleAttribute(target: HTMLElement): string {
        const role = target.getAttribute?.('role');
        return role ? `[role=${role}]` : '';
    }

    private formatTargetText(target: HTMLElement): string {
        if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
            return '';
        }

        const text = (target.textContent || '').replace(/\s+/g, ' ').trim();
        if (!text) {
            return '';
        }

        const clipped = text.length > 80 ? `${text.slice(0, 77)}...` : text;
        return ` "${clipped}"`;
    }

    private getInputLength(target: HTMLElement | null): number | undefined {
        if (!target) {
            return undefined;
        }

        if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) {
            return target.value?.length ?? 0;
        }

        return undefined;
    }
}